/**
 * KhiMyDataHub — „Моите данни" за Kidney Health Intelligence.
 *
 * Аналог на BHI MyDataHub: единна точка за качване на документи
 * (UnifiedDocumentUpload) + статус плочки за бъбречните входящи данни.
 * Лабораториите са първата плочка — те са централният модул.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Database, ChevronRight, FlaskConical, Activity, CheckCircle2, Circle,
  AlertCircle, Dna, FileText, Stethoscope, HeartPulse, Pill,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import UnifiedDocumentUpload from "@/components/breast_intel/UnifiedDocumentUpload";

const PANEL_KEYS = ["serum_k", "serum_p", "serum_ca", "pth", "bicarbonate", "hemoglobin", "ferritin", "acr"];

function StatusPill({ status }) {
  const cfg = {
    complete: { Icon: CheckCircle2, cls: "text-emerald-500", label: "Добавени" },
    partial: { Icon: AlertCircle, cls: "text-amber-500", label: "Частично" },
    missing: { Icon: Circle, cls: "text-slate-400", label: "Липсват" },
  }[status] || { Icon: Circle, cls: "text-slate-400", label: "Липсват" };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

export default function KhiMyDataHub({ dash, genetics, onUploaded }) {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadHint, setUploadHint] = useState("");

  const labs = dash?.labs || {};
  const panelFilled = PANEL_KEYS.filter((k) => labs[k] != null).length;
  const panelStatus = panelFilled >= 5 ? "complete" : panelFilled >= 1 ? "partial" : "missing";
  const egfrStatus = dash?.egfr != null ? "complete" : "missing";
  const etioStatus = (dash?.declared_etiologies?.length || 0) > 0 ? "complete" : "missing";
  const dnaStatus = genetics?.family_pkd_declared ? "partial" : "missing";

  const items = [
    {
      key: "labs", Icon: FlaskConical, color: "text-blue-500", status: panelStatus,      label: "Лаборатории (централен модул)",
      desc: "Всички Ваши изследвания на едно място — от тук се захранват eGFR, калий, фосфор, PTH, бикарбонат и албумин/креатинин.",
      cta: "Отвори лаборатории", route: "/dashboard/analyses",
    },
    {
      key: "egfr", Icon: Activity, color: "text-cyan-500", status: egfrStatus,
      label: "eGFR / креатинин",
      desc: "Базата за KDIGO стадия и всички дневни лимити. Качете лабораторен PDF или въведете eGFR ръчно по-долу.",
      cta: "Качете изследване", openUpload: true, hint: "standard_lab",
    },
    {
      key: "panel", Icon: HeartPulse, color: "text-emerald-500", status: panelStatus,
      label: "Бъбречен набор изследвания (калий, фосфор, калций, паратхормон, бикарбонат, хемоглобин, белтък в урината)",
      desc: "Пълният набор включва в анализа костно-минералното нарушение, вкисляването на кръвта и бъбречната анемия.",
      cta: "Качи изследванията", openUpload: true, hint: "standard_lab",
    },
    {
      key: "etiology", Icon: Stethoscope, color: "text-amber-500", status: etioStatus,
      label: "Бъбречни диагнози и етиология",
      desc: "Диабетна нефропатия, хипертензивна нефросклероза, PKD, гломерулонефрит, обструкция — декларирайте ги в профила си.",
      cta: "Редактирай профил", route: "/dashboard/profile",
    },
    {
      key: "dna", Icon: Dna, color: "text-purple-500", status: dnaStatus,
      label: "ДНК (PKD1/PKD2, APOL1)",
      desc: "Качете генетичен отчет — изкуственият интелект разпознава бъбречните варианти и ги показва в наследствения контекст.",
      cta: "Качи ДНК тест", openUpload: true, hint: "genomic",
    },
    {
      key: "medications", Icon: Pill, color: "text-violet-500", status: "missing",
      label: "Лекарства и добавки",
      desc: "Проверяваме комбинациите Ви за бъбречни рискове (обезболяващи, метформин, калий, фосфат-свързватели).",
      cta: "Отвори проверката", route: "/dashboard/medications",
    },
    {
      key: "epicrisis", Icon: FileText, color: "text-rose-500", status: "missing",
      label: "Епикризи и нефрологични доклади",
      desc: "Епикризи от нефролог/диализен център (PDF, снимка) се прикачват към профила Ви.",
      cta: "Качи епикриза", openUpload: true, hint: "epicrisis",
    },
  ];

  const handleClick = (it) => {
    if (it.openUpload) { setUploadHint(it.hint || ""); setUploadOpen(true); return; }
    navigate(it.route);
  };

  const summary = `${items.filter((i) => i.status === "complete").length}/${items.length}`;
  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`p-5 rounded-2xl border ${card}`} data-testid="khi-my-data-hub">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
          <Database className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-extrabold">📥 Моите данни</h3>
            <span className="text-xs font-bold text-cyan-500" data-testid="khi-my-data-summary">{summary}</span>
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Колкото повече бъбречни данни добавите — толкова по-точни са стадият, лимитите и прогнозите.
            Всичко е под Ваш контрол и може да бъде изтрито по всяко време.
          </p>
          <p className="text-[10px] mt-1 font-semibold text-cyan-500">
            Данните са общи за всички модули — качена тук епикриза или изследване се вижда
            и в гърдата, черния дроб и профила. Целият списък е в профила → Моите данни.
          </p>
        </div>
      </div>

      <button
        onClick={() => { setUploadHint(""); setUploadOpen(true); }}
        className="mt-3 w-full px-4 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 flex items-center justify-center gap-2 shadow-md"
        data-testid="khi-my-data-upload-any"
      >
        <FileText className="w-4 h-4" />
        Качи документ (лаборатория, ДНК, епикриза)
      </button>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => handleClick(it)}
            className={`p-3 rounded-xl border text-left transition-all hover:shadow-md ${
              isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"
            }`}
            data-testid={`khi-my-data-${it.key}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <it.Icon className={`w-4 h-4 ${it.color}`} />
              <StatusPill status={it.status} />
            </div>
            <div className="text-xs font-bold leading-snug">{it.label}</div>
            <div className={`text-[10px] leading-snug mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{it.desc}</div>
            <div className="text-[10px] mt-1 inline-flex items-center gap-0.5 text-cyan-500 font-semibold">
              {it.cta} <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        ))}
      </div>

      <UnifiedDocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lang="bg"
        initialHint={uploadHint}
        onDone={(data) => {
          onUploaded?.(data);
          try { window.dispatchEvent(new CustomEvent("bhi:document:uploaded", { detail: data })); } catch (_) { /* noop */ }
        }}
      />
    </div>
  );
}
